import UAParser from 'ua-parser-js'

import {
  createTimeoutPromise,
  debouncePromise,
} from '@flamingo_tech/funkgo-utils/promiseUtils'

import createListener from '../utils/createListener'

let callbackHandlers = {}
let options

const getBridgeInstance = () => window.WebViewJavascriptBridge || window.WKWebViewJavascriptBridge

const installBridge = () => createTimeoutPromise(resolve => {
  window.WVJBCallbacks = window.WKWVJBCallbacks = [resolve]

  if (window.WKWebViewJavascriptBridge) {
    window.webkit.messageHandlers.iOS_Native_InjectJavascript.postMessage(null)
  } else {
    const bridgeSrc = options && options.ios
      ? 'wvjbscheme://__BRIDGE_LOADED__'
      : 'https://__bridge_loaded__'

    const WVJBIframe = document.createElement('iframe')

    WVJBIframe.style.display = 'none'
    WVJBIframe.src = bridgeSrc
    document.documentElement.appendChild(WVJBIframe)
    setTimeout(() => { document.documentElement.removeChild(WVJBIframe) }, 0)
  }
}, 20000, 'appSdk:install')

const setupBridge = debouncePromise(() => {
  const bridge = getBridgeInstance()

  if (bridge) {
    return Promise.resolve(bridge)
  } else {
    return installBridge().then(getBridgeInstance)
  }
})

/* ----------------------------------------------- */

const init = initOptions => options = initOptions
const install = setupBridge

const ensureBridgeConnectPromise = (task, timeout, debugFlag) => {
  return createTimeoutPromise((resolve, reject) => {
    setupBridge().then(
      bridge => task(bridge, resolve, reject),
      err => reject(err)
    )
  }, timeout, debugFlag)
}

const ensureBridgeConnectPromiseWithoutTimeoutReject = (task, timeout, debugFlag) => {
  return createTimeoutPromise((resolve, reject) => {
    setupBridge().then(
      bridge => task(bridge, resolve, reject),
      err => reject(err)
    )
  }, timeout, debugFlag, false)
}

/* ----------------------------------------------- */

const handleAppResponse = res => {
  let resData = res

  try {
    if (typeof resData === 'string') {
      resData = JSON.parse(resData)
    }
  } catch (error) {
    return Promise.reject(error)
  }

  const code = parseInt(resData.code, 10)
  const data = resData.data || {}
  const message = resData.message

  if (code === 200) {
    return Promise.resolve(data)
  } else {
    let err = new Error(message)
    err.errorCode = data && parseInt(data.code, 10)
    return Promise.reject(err)
  }
}

/* ----------------------------------------------- */

const DEFAULT_BRIDGE_TIMEOUT = 1000 * 10
const LOGIN_BRIDGE_TIMEOUT = 1000 * 60 * 10

// 临时兼容Android 判断UA
const loginTimeout = () => {
  if (window && window.navigator && window.navigator.userAgent) {
    const parser = new UAParser(window.navigator.userAgent)
    const { os } = parser.getResult()
    if (os.name === 'Android') {
      return 1000 * 1
    }
  }
  return 1000 * 60 * 10
}

const callHandler = (method, payload = {}, timeout = DEFAULT_BRIDGE_TIMEOUT) => {
  const task = (bridge, resolve, reject) => {
    bridge.callHandler(method, payload,
      res => {
        handleAppResponse(res).then(
          resolve,
          reject
        )
      }
    )
  }
  return ensureBridgeConnectPromise(task, timeout, `bridge:${method}`)
}
/* ----------------------------------------------- */

const createCallbackListener = method => {
  if (!callbackHandlers[method]) {
    callbackHandlers[method] = createListener()

    const task = (bridge, resolve) => {
      bridge.registerHandler(method, data => {
        callbackHandlers[method].notify(data)
      })
      resolve()
    }

    return ensureBridgeConnectPromise(task, DEFAULT_BRIDGE_TIMEOUT, `subscribeBridge:${method}`)
  }
  return Promise.resolve()
}

const subscribe = (method, callback) => {
  const result = createCallbackListener(method)
  callbackHandlers[method].subscribe(callback)
  return result
}

const unsubscribe = (method, callback) => {
  const result = createCallbackListener(method)
  callbackHandlers[method].unsubscribe(callback)
  return result
}

/* ----------------------------------------------- */

// like login, it will open new webview and enter the login flow; once login flow finished
//   , at iOS - it will call the callback with 'login'
//   , at android - it will dispatch another message, which named "postLoginResults"

const callAsyncHandler = (method, callbackMethod, payload = {}) => {
  const task = (bridge, resolve, reject) => {
    let onAsyncResponse
    let teardown
    let setup

    /* ----------------------------------------------------- */

    onAsyncResponse = (res) => {
      handleAppResponse(res).then(resolve, reject)
      if (teardown) {
        teardown()
        teardown = null
      }
    }

    teardown = () => {
      if (onAsyncResponse) {
        unsubscribe(callbackMethod, onAsyncResponse)
        onAsyncResponse = null
      }
    }

    setup = () => {
      if (onAsyncResponse) {
        subscribe(callbackMethod, onAsyncResponse)
      }
    }

    /* ----------------------------------------------------- */

    // setup an async callback to handle android bridge response for: login() & loginByFb()
    setup()

    callHandler(method, payload, LOGIN_BRIDGE_TIMEOUT)
      .then(
        resolve,
        reject
      )
      .then(
        teardown
      )
  }

  const LOGIN_BRIDGE_ASYNC_HANDLER_TIMEOUT = loginTimeout() // 临时兼容Android Login fail没有callback

  return ensureBridgeConnectPromiseWithoutTimeoutReject(task, LOGIN_BRIDGE_ASYNC_HANDLER_TIMEOUT, `async bridge:${method}`)
}

/* ----------------------------------------------- */
const asyncMethodsMap = {
  login: {
    asyncCallbackName: 'postLoginResults'
  }
}

const methodsMap = [
  'getUserInfo',
  'vibrate',

  'share',

  'closeWindow',
  'navigate',

  'callAppStoreRate',
  'requestNotifyAuth',
  'getNotificationInfo',
  'openApplicationSetting',
  'requestAppStoreReview',
  'shareImage',
  'trackAppsFlyer',
  'anonymousUserInfoWebToApp',
  'anonymousUserAddressWebToApp',

  'getDeviceInfo',
  'getPushToken',

  'getCartId',
  'getCouponCenterId',
  'refreshCart',
  'refreshCouponCenter'
]

// if getUserInfo return "isNotLogin" error, then no needed to track
const skipTrackMap = {
  getUserInfo: [
    'isNotLogin'
  ]
}

/* ----------------------------------------------- */

const bridges = {}
Object.keys(asyncMethodsMap).forEach(name => {
  const asyncCallbackName = asyncMethodsMap[name].asyncCallbackName

  bridges[name] = payload => callAsyncHandler(name, asyncCallbackName, payload)
})

methodsMap.forEach(name => {
  bridges[name] = payload => callHandler(name, payload, DEFAULT_BRIDGE_TIMEOUT).catch(err => {
    if (skipTrackMap[name]) {
      if (skipTrackMap[name].indexOf(err.message) > -1) {
        err._skipTrack = true
      }
    }

    throw err
  })
})


bridges.subscribe = subscribe
bridges.unsubscribe = unsubscribe

export {
  init,
  install,
  bridges,
}
